import { tokens } from '@sparkpost/design-tokens';
import moment from 'moment';
import React from 'react';
import {
  Area,
  AxisDomain,
  Bar,
  ComposedChart,
  ItemSorter,
  LabelFormatter,
  Line,
  LineProps,
  RechartsFunction,
  ResponsiveContainer,
  ScaleType,
  TickFormatterFunction,
  Tooltip,
  TooltipFormatter,
  TooltipPayload,
  XAxis,
  YAxis
} from 'recharts';
import { DOTTED_LINE_PREFIX_KEY } from 'src/constants';
import Cursor from './Cursor';
import styles from './LineChart.module.scss';

const identity = (a: {}) => a;

const orderDesc: ItemSorter<TooltipPayload> = (a: $TODOFIXME, b: $TODOFIXME) => {
  return b.value - a.value;
};

interface SPLineProps extends LineProps {
  dataKey: number;
}

type SPLineChartProps = {
  data: $TODOFIXME[];
  height: number;
  labelForDottedLine: $TODOFIXME;
  lines: SPLineProps[] | Readonly<SPLineProps[]>;
  precision: 'day' | '1min' | '15min' | 'month';
  showLastLineAsDottedLine: boolean;
  showTooltip: boolean;
  showXAxis: boolean;
  syncId: number;
  tooltip: React.ElementType;
  tooltipLabelFormatter: LabelFormatter | undefined;
  tooltipValueFormatter: TooltipFormatter | undefined;
  yLabel: string;
  xAxisKey: string;
  xTickFormatter: TickFormatterFunction | undefined;
  yDomain: readonly [AxisDomain, AxisDomain] | undefined;
  yTickFormatter: TickFormatterFunction | undefined;
  unit: string;
  yScale: RechartsFunction | ScaleType | undefined;
};

export default function SpLineChart(props: SPLineChartProps) {
  const formatChartData = (data: $TODOFIXME[]) => {
    const { showLastLineAsDottedLine, lines } = props;
    if (showLastLineAsDottedLine && data.length > 2) {
      lines.forEach((x) => {
        data[data.length - 2][DOTTED_LINE_PREFIX_KEY + x.dataKey] =
          data[data.length - 2][x.dataKey];

        data[data.length - 1][DOTTED_LINE_PREFIX_KEY + x.dataKey] =
          data[data.length - 1][x.dataKey];
        delete data[data.length - 1][x.dataKey];
      });
    }
    return data;
  };

  const renderLines = () => {
    const { lines = [], showLastLineAsDottedLine } = props;

    return lines.map((line: $TODOFIXME) => {
      const lineProps = {
        strokeWidth: 2,
        animationDuration: 400,
        activeDot: false,
        dot: false,
        type: 'linear',
        connectNulls: true,
        ...line
      };
      if (showLastLineAsDottedLine) {
        return [
          <Line {...lineProps} />,
          <Line
            {...lineProps}
            dataKey={DOTTED_LINE_PREFIX_KEY + lineProps.dataKey}
            key={DOTTED_LINE_PREFIX_KEY + lineProps.dataKey}
            strokeDasharray="3 3"
          />
        ];
      }
      return <Line {...lineProps} />;
    });
  };

  // Manually generates X axis ticks
  const getXTicks = () => {
    const { data, precision } = props;
    let ticks;

    // Shows ticks every Sunday
    if (precision === 'day' && data.length > 15) {
      ticks = data.reduce((acc, { ts }) => {
        if (moment(ts).isoWeekday() === 7) {
          acc.push(ts);
        }
        return acc;
      }, []);
    }

    // Show ticks every 15 minutes
    if (precision === '1min') {
      ticks = data.reduce((acc, { ts }) => {
        if (moment(ts).minutes() % 15 === 0) {
          acc.push(ts);
        }
        return acc;
      }, []);
    }

    // Show ticks every 30 minutes
    if (precision === '15min') {
      ticks = data.reduce((acc, { ts }) => {
        if (moment(ts).minutes() % 30 === 0) {
          acc.push(ts);
        }
        return acc;
      }, []);
    }

    return ticks;
  };

  const {
    data,
    height,
    syncId,
    showTooltip,
    xTickFormatter = identity,
    yTickFormatter = identity,
    yScale = 'linear',
    yDomain = ['dataMin', 'dataMin'],
    tooltipLabelFormatter = identity,
    tooltipValueFormatter = identity,
    showXAxis,
    xAxisKey = 'ts',
    yLabel,
    tooltip: CustomTooltip,
    unit,
    labelForDottedLine,
    showLastLineAsDottedLine
  } = props;

  const xAxisHeight = 30;
  const formattedChartData = formatChartData(data);
  return (
    <div className={styles.ChartWrapper}>
      <ResponsiveContainer width="99%" height={showXAxis ? height + xAxisHeight : height}>
        <ComposedChart syncId={syncId} barCategoryGap="3%" data={formattedChartData}>
          <Bar key="noKey" dataKey="noKey" background={{ fill: tokens.color_gray_200 }} />
          <XAxis
            axisLine={false}
            dataKey={xAxisKey}
            height={xAxisHeight}
            hide={!showXAxis}
            interval="preserveStartEnd"
            tickFormatter={xTickFormatter}
            tickLine={false}
            ticks={getXTicks()}
          />
          <YAxis
            axisLine={false}
            domain={yDomain}
            interval="preserveStartEnd"
            padding={{ top: 8, bottom: 8 }}
            scale={yScale}
            tickFormatter={yTickFormatter}
            tickLine={false}
            width={60}
          />
          <Tooltip
            cursor={<Cursor data={formattedChartData} />}
            content={
              CustomTooltip ? (
                <CustomTooltip
                  data={formattedChartData}
                  showTooltip={showTooltip}
                  labelForDottedLine={showLastLineAsDottedLine ? labelForDottedLine : undefined}
                />
              ) : undefined
            }
            wrapperStyle={{ zIndex: tokens.zIndex_overlay }}
            isAnimationActive={false}
            itemSorter={orderDesc}
            labelFormatter={tooltipLabelFormatter}
            formatter={tooltipValueFormatter}
          />
          {renderLines()}
          {unit === 'percent' && (
            <Area
              dataKey="industry_rate"
              stroke={tokens.color_blue_800}
              fill={tokens.color_blue_800}
              opacity={0.15}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <span className="sp-linechart-yLabel">{yLabel}</span>
    </div>
  );
}
